import axios from 'axios';
import { createCoalescer, paramsKey } from '../utils/requestCoalescer';
import {
  getAccessToken,
  getRefreshToken,
  updateAccessToken,
  clearTokens,
  getImpersonation,
} from './authStorage';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Ensure baseURL is always relative to current origin
api.defaults.baseURL = '/api';

// Map legacy `target` payloads to the new `targets[]` shape the server expects (#689).
// Forms still build a singular `target` object internally; this normalizes on the way out.
function normalizeObservationPayload(data) {
  if (!data || typeof data !== 'object') return data;
  if (Array.isArray(data.targets) && data.targets.length > 0) {
    const { target, ...rest } = data;
    return rest;
  }
  if (data.target && !data.targets) {
    const { target, ...rest } = data;
    return { ...rest, targets: [target] };
  }
  return data;
}

// --- Server unavailable toast ---
let toastElement = null;
let toastTimeout = null;

function showServerToast(message) {
  if (!toastElement) {
    toastElement = document.createElement('div');
    toastElement.id = 'server-unavailable-toast';
    Object.assign(toastElement.style, {
      position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
      backgroundColor: '#d32f2f', color: '#fff', padding: '12px 24px',
      borderRadius: '8px', zIndex: '9999', fontSize: '14px', fontFamily: 'inherit',
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)', transition: 'opacity 0.3s',
      opacity: '0', pointerEvents: 'none',
    });
    document.body.appendChild(toastElement);
  }
  toastElement.textContent = message;
  toastElement.style.opacity = '1';
  clearTimeout(toastTimeout);
  toastTimeout = setTimeout(() => { toastElement.style.opacity = '0'; }, 8000);
}

function hideServerToast() {
  if (toastElement) toastElement.style.opacity = '0';
}

// --- Retry logic for server unavailability ---
const RETRY_STATUS_CODES = [502, 503, 504];
const MAX_RETRIES = 3;
const RETRY_DELAYS = [2000, 4000, 8000]; // exponential backoff

api.interceptors.response.use(null, async (error) => {
  const config = error.config;
  if (!config) return Promise.reject(error);

  const isNetworkError = !error.response && error.code !== 'ECONNABORTED';
  const isServerDown = error.response && RETRY_STATUS_CODES.includes(error.response.status);

  if ((isNetworkError || isServerDown) && !config._retryCount) {
    config._retryCount = 0;
  }

  if ((isNetworkError || isServerDown) && config._retryCount < MAX_RETRIES) {
    config._retryCount++;
    const delay = RETRY_DELAYS[config._retryCount - 1] || 8000;
    showServerToast(`Server temporarily unavailable — retrying (${config._retryCount}/${MAX_RETRIES})...`);
    await new Promise(resolve => setTimeout(resolve, delay));
    return api(config);
  }

  if ((isNetworkError || isServerDown) && config._retryCount >= MAX_RETRIES) {
    showServerToast('Server is unavailable. Please try again in a few minutes.');
  }

  // If a retry succeeded earlier, clear the toast
  return Promise.reject(error);
});

// Clear toast on successful responses after retries
api.interceptors.response.use((response) => {
  if (response.config._retryCount > 0) {
    hideServerToast();
  }
  return response;
});

// Track if we're currently refreshing to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Identical observatory-list reads share one request for up to 10s (see requestCoalescer.js:
// the Control Center's cards and panes each load the list). Any write under /v1/observatories
// drops the shared results, so a change shows on the next read rather than after the TTL.
const observatoryListReads = createCoalescer({ ttlMs: 10000 });

// Request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    if ((config.method || 'get').toLowerCase() !== 'get' && String(config.url || '').startsWith('/v1/observatories')) {
      observatoryListReads.clear();
    }
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor with automatic token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh if this IS the refresh request or login request
      if (originalRequest.url?.includes('/auth/refresh') ||
          originalRequest.url?.includes('/auth/login')) {
        // For login requests, just clean up tokens but don't redirect
        // Let the login component handle the error display
        if (originalRequest.url?.includes('/auth/login')) {
          clearTokens();
          return Promise.reject(error);
        }

        // For refresh requests, redirect to login
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue requests while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        // No refresh token, redirect to login
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post('/api/v1/auth/refresh', {
          refreshToken: refreshToken
        });

        if (response.data.success) {
          const { accessToken, refreshToken: newRefreshToken } = response.data.data;

          // Store new tokens in whichever scope the refresh token came from
          updateAccessToken(accessToken, newRefreshToken);

          // Update axios default header
          axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

          // Process queued requests
          processQueue(null, accessToken);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } else {
          throw new Error('Refresh failed');
        }
      } catch (refreshError) {
        processQueue(refreshError, null);

        // Only force a logout when the refresh was DEFINITIVELY rejected:
        //   401 = refresh token itself is invalid/expired/wrong-type/user-gone
        //   400 = no/malformed refresh token (broken token state)
        // Any OTHER failure is transient and must NOT log the user out — a
        // network error, a request timeout, a 429, or a 5xx (e.g. the API
        // briefly CPU-starved during a bulk download, which returns 500 from
        // the refresh handler). In those cases we keep the tokens and let a
        // subsequent request retry; otherwise a momentary server hiccup
        // silently kicks the user to the login screen.
        const status = refreshError.response?.status;
        if (status === 401 || status === 400) {
          clearTokens();
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Enhance error object with more details for debugging
    error.apiDetails = {
      endpoint: error.config?.url,
      method: error.config?.method,
      status: error.response?.status
    };

    return Promise.reject(error);
  }
);

export const apiService = {
  // Health check
  async checkHealth() {
    const response = await api.get('/v1/health');
    return response.data;
  },

  // Observatory management
  // One page of GET /v1/observatories, exactly as asked for. The server defaults to 10 rows
  // and caps a page at 100.
  async getObservatoriesPage(params = {}) {
    return observatoryListReads.get(paramsKey(params), async () => {
      const response = await api.get('/v1/observatories', { params });
      return response.data;
    });
  },

  // Observatories the caller can see. With an explicit `page` or `limit` this is that one page
  // (pagers, the map, the `limit: 1` existence check). WITHOUT either it returns EVERY
  // observatory: the endpoint's default page of 10 used to apply silently, so ~30 pickers and
  // lists — the submit-observation dialog among them — dropped the 11th observatory onward.
  // Same envelope as one page; pagination then describes the whole set as a single page.
  async getObservatories(params = {}) {
    if (params.page != null || params.limit != null) return this.getObservatoriesPage(params);

    const PAGE_SIZE = 100;
    const first = await this.getObservatoriesPage({ ...params, page: 1, limit: PAGE_SIZE });
    const firstRows = first?.data?.observatories;
    if (!Array.isArray(firstRows)) return first;        // unexpected shape: pass it through untouched

    const totalPages = first.data.pagination?.total_pages || 1;
    let all = firstRows;
    if (totalPages > 1) {
      const rest = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          this.getObservatoriesPage({ ...params, page: i + 2, limit: PAGE_SIZE })
            .then((r) => r?.data?.observatories || [])
            .catch(() => [])),
      );
      all = firstRows.concat(...rest);
    }

    // A row can shift across a page boundary between requests; keep the first occurrence.
    const seen = new Set();
    const observatories = all.filter((o) => {
      const id = o && o._id != null ? String(o._id) : '';
      if (!id) return true;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });

    const pg = first.data.pagination || {};
    return {
      ...first,
      data: {
        ...first.data,
        observatories,
        pagination: {
          ...pg,
          current_page: 1,
          total_pages: 1,
          has_next: false,
          has_previous: false,
          limit: observatories.length,
          total_count: pg.total_count != null ? pg.total_count : observatories.length,
        },
      },
    };
  },

  async getObservatory(id) {
    const response = await api.get(`/v1/observatories/${id}`);
    return response.data;
  },

  async createObservatory(data) {
    const response = await api.post('/v1/observatories/admin/create', data);
    return response.data;
  },

  async updateObservatory(id, data) {
    const response = await api.put(`/v1/observatories/${id}`, data);
    return response.data;
  },

  // Apply configuration and instruct the plugin to restart NINA, similar to
  // the plugin-version-update flow. Returns { success, data: { message_sent } }
  async applyObservatoryAndRestart(id, data) {
    const response = await api.post(`/v1/observatories/${id}/apply-and-restart`, data);
    return response.data;
  },

  async deleteObservatory(id, { comment } = {}) {
    const params = comment ? { comment } : {};
    const response = await api.delete(`/v1/observatories/${id}`, { params });
    return response.data;
  },

  // Get all observations for an observatory (uses administrative endpoint for full visibility)
  async getObservatoryObservations(id, params = {}) {
    // Try administrative endpoint first (returns all obs for observatories user owns/admins)
    try {
      const response = await api.get('/v1/observations/administrative', {
        params: { observatory_id: id, limit: 100, ...params },
      });
      return response.data?.data?.observations || response.data?.data || [];
    } catch (e) {
      // Fall back to regular endpoint
      const response = await api.get('/v1/observations', { params: { limit: 100, ...params } });
      const allObs = response.data?.data?.observations || response.data?.data || [];
      return (Array.isArray(allObs) ? allObs : []).filter(obs => {
        const assignedId = obs.assigned_to?._id || obs.assigned_to;
        return assignedId === id;
      });
    }
  },

  // Current live observation (in_progress/assigned) for EVERY observatory the user can see,
  // in ONE request (#1445). Replaces the per-observatory fan-out that fired one
  // /administrative call per observatory. Returns a map keyed by observatory id.
  async getCurrentObservationsByObservatory() {
    const response = await api.get('/v1/observations/current-by-observatory');
    return response.data?.data?.current_by_observatory || {};
  },

  // Get current observation for an observatory
  async getObservatoryCurrentObservation(id) {
    const observations = await this.getObservatoryObservations(id);
    const currentObs = Array.isArray(observations) ?
      observations.find(obs =>
        (obs.status === 'in_progress' || obs.status === 'assigned') &&
        (obs.assigned_to?._id === id || obs.assigned_to === id)
      ) : null;
    
    return currentObs;
  },

  // Observatory sessions and analytics
  async getObservatorySessions(id, params = {}) {
    // Note: Using /history endpoint since /sessions doesn't exist yet
    const response = await api.get(`/v1/observatories/${id}/history`, { params });
    return response.data;
  },

  async getObservatoryHistory(id, params = {}) {
    const response = await api.get(`/v1/observatories/${id}/history`, { params });
    return response.data;
  },

  async getObservatoryAnalytics(id, timeframe = 'monthly') {
    const response = await api.get(`/v1/observatories/${id}/analytics`, {
      params: { timeframe }
    });
    return response.data;
  },

  // Observatory member management
  async getObservatoryMembers(observatoryId) {
    const response = await api.get(`/v1/observatories/${observatoryId}/members`);
    return response.data;
  },

  // Legacy role-based method (still works for backward compatibility)
  async addObservatoryMember(observatoryId, userId, role) {
    const response = await api.post(`/v1/observatories/${observatoryId}/members`, {
      user_id: userId,
      role: role
    });
    return response.data;
  },

  // New permission-based method
  async addObservatoryMemberWithPermissions(observatoryId, userId, permissions) {
    const response = await api.post(`/v1/observatories/${observatoryId}/members`, {
      user_id: userId,
      permissions: permissions
    });
    return response.data;
  },

  // Add all members of an organization to an observatory
  async addObservatoryOrganizationMembers(observatoryId, organizationId, permissions) {
    const response = await api.post(`/v1/observatories/${observatoryId}/members/organization`, {
      organization_id: organizationId,
      permissions: permissions
    });
    return response.data;
  },

  // Update permissions for all members added via an organization
  async updateObservatoryOrganizationPermissions(observatoryId, organizationId, permissions) {
    const response = await api.put(`/v1/observatories/${observatoryId}/members/organization/${organizationId}`, {
      permissions: permissions
    });
    return response.data;
  },

  // Remove all members added via an organization
  async removeObservatoryOrganization(observatoryId, organizationId) {
    const response = await api.delete(`/v1/observatories/${observatoryId}/members/organization/${organizationId}`);
    return response.data;
  },

  // Legacy role-based method (still works for backward compatibility)
  async updateObservatoryMemberRole(observatoryId, userId, role) {
    const response = await api.put(`/v1/observatories/${observatoryId}/members/${userId}`, {
      role: role
    });
    return response.data;
  },

  // New permission-based method
  async updateObservatoryMemberPermissions(observatoryId, userId, permissions) {
    const response = await api.put(`/v1/observatories/${observatoryId}/members/${userId}`, {
      permissions: permissions
    });
    return response.data;
  },

  async removeObservatoryMember(observatoryId, userId) {
    const response = await api.delete(`/v1/observatories/${observatoryId}/members/${userId}`);
    return response.data;
  },

  async cleanupObservatoryMembers(observatoryId) {
    const response = await api.post(`/v1/observatories/${observatoryId}/cleanup-members`);
    return response.data;
  },

  async getObservatoryAccessLevel(observatoryId) {
    const response = await api.get(`/v1/observatories/${observatoryId}/access-level`);
    return response.data;
  },

  async uploadObservatoryLogo(observatoryId, file) {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await api.post(`/v1/observatories/${observatoryId}/branding/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async deleteObservatoryLogo(observatoryId) {
    const response = await api.delete(`/v1/observatories/${observatoryId}/branding/logo`);
    return response.data;
  },

  async transferObservatoryOwnership(observatoryId, toType, toId, reason = null) {
    const response = await api.post(`/v1/observatories/${observatoryId}/transfer`, {
      to_type: toType,
      to_id: toId,
      reason: reason
    });
    return response.data;
  },

  // API Key management
  async generateApiKey(observatoryId) {
    const response = await api.post(`/v1/observatories/${observatoryId}/api-key`);
    return response.data;
  },

  async generateTemporaryApiKey(observatoryId, daysValid = 14) {
    const response = await api.post(`/v1/observatories/${observatoryId}/api-key/temporary`, {
      days_valid: daysValid
    });
    return response.data;
  },

  async generateExtendedApiKey(observatoryId, monthsValid = 12) {
    const response = await api.post(`/v1/observatories/${observatoryId}/api-key/extended`, {
      months_valid: monthsValid
    });
    return response.data;
  },

  async revokeApiKey(observatoryId) {
    const response = await api.delete(`/v1/observatories/${observatoryId}/api-key`);
    return response.data;
  },

  async notifyApiKeyUpdate(observatoryId, reason = null) {
    const response = await api.post(`/v1/observatories/${observatoryId}/notify-api-key-update`, {
      reason: reason
    });
    return response.data;
  },

  async checkPendingApiKeyStatus(observatoryId) {
    const response = await api.get(`/v1/observatories/${observatoryId}/pending-api-key/status`);
    return response.data;
  },

  // PhotonRanch: the two site codes, and why an observatory is or is not
  // publishing. Server admins only, enforced server-side.
  async getPtrStatus(observatoryId) {
    const response = await api.get(`/v1/observatories/${observatoryId}/ptr-status`);
    return response.data;
  },

  async updatePtrSettings(observatoryId, settings) {
    const response = await api.put(`/v1/observatories/${observatoryId}/ptr-settings`, settings);
    return response.data;
  },

  async markPendingApiKeyProvided(observatoryId) {
    const response = await api.post(`/v1/observatories/${observatoryId}/pending-api-key/mark-provided`);
    return response.data;
  },

  async resetObservatoryFingerprint(observatoryId) {
    const response = await api.post(`/v1/observatories/${observatoryId}/reset-fingerprint`);
    return response.data;
  },

  async toggleObservatoryDispatch(observatoryId, dispatchEnabled) {
    const response = await api.put(`/v1/observatories/${observatoryId}/dispatch`, {
      dispatch_enabled: dispatchEnabled
    });
    return response.data;
  },

  async toggleObservatoryAutomation(observatoryId, automationEnabled) {
    const response = await api.put(`/v1/observatories/${observatoryId}/automation`, {
      automation_enabled: automationEnabled
    });
    return response.data;
  },

  // Direct Control windows (an observer books time to drive the telescope interactively).
  // start_time: ISO UTC on a quarter hour; duration_minutes: 15..90 in 15-minute steps.
  async createDirectControl(observatoryId, { start_time, duration_minutes }) {
    const response = await api.post(`/v1/observatories/${observatoryId}/direct-control`, { start_time, duration_minutes });
    return response.data;
  },

  // Dark time for Direct Control on a UTC day: { dark_time_bypassed, twilight_type, has_location, dark_windows }.
  async getDirectControlAvailability(observatoryId, date) {
    const response = await api.get(`/v1/observatories/${observatoryId}/direct-control/availability`, { params: { date } });
    return response.data;
  },

  async cancelDirectControl(reservationId, cancellation_reason) {
    const response = await api.post(`/v1/direct-control/${reservationId}/cancel`, cancellation_reason ? { cancellation_reason } : {});
    return response.data;
  },

  async toggleObservatoryRemoteControl(observatoryId, realTimeEnabled) {
    const response = await api.put(`/v1/observatories/${observatoryId}/remote-control`, {
      realTimeEnabled: realTimeEnabled
    });
    return response.data;
  },

  // Real-time control allowlist (explicit per-individual authorization for RT control).
  async getObservatoryRealtimeUsers(observatoryId) {
    const response = await api.get(`/v1/observatories/${observatoryId}/realtime-users`);
    return response.data;
  },
  async setObservatoryRealtimeUsers(observatoryId, { user_ids = [], emails = [] } = {}) {
    const response = await api.put(`/v1/observatories/${observatoryId}/realtime-users`, { user_ids, emails });
    return response.data;
  },

  async getApiKeyInfo(observatoryId) {
    const response = await api.get(`/v1/observatories/${observatoryId}/api-key`);
    return response.data;
  },

  // System analytics
  async getSystemAnalytics(timeframe = 'monthly') {
    const response = await api.get('/v1/analytics/system', {
      params: { timeframe }
    });
    return response.data;
  },

  async getObservationStatistics(timeframe = 'monthly') {
    const response = await api.get('/v1/analytics/observations', {
      params: { timeframe }
    });
    return response.data;
  },

  // Performance observability (server-admin). Populated only when the server's
  // REQUEST_LOG_PERSIST / DOCKER_STATS_SAMPLER flags are on; otherwise the
  // endpoints return empty results (not errors).
  // Ranked endpoints + summary. Accepts either a now-relative `hours` or an explicit
  // `from`/`to` (ISO) custom window (bounded client-side by the request_logs TTL).
  async getPerformanceReport({ hours, from, to } = {}) {
    const response = await api.get('/v1/admin/logs/performance', {
      params: { hours, from, to }
    });
    return response.data;
  },

  async getPerformanceCorrelation({ from, to, bucket } = {}) {
    const response = await api.get('/v1/admin/logs/performance/correlation', {
      params: { from, to, bucket }
    });
    return response.data;
  },

  // Per-observatory WebSocket throughput (from comms_stats). Populated only when
  // COMMS_STATS_SAMPLER=on. Accepts now-relative `hours` or explicit `from`/`to`.
  async getPerformanceComms({ hours, from, to } = {}) {
    const response = await api.get('/v1/admin/logs/performance/comms', {
      params: { hours, from, to }
    });
    return response.data;
  },

  // Live observatory WebSocket connections straight from the CommunicationsEngine.
  // Live state (not sampled) — available regardless of the sampler flags.
  async getPerformanceConnections() {
    const response = await api.get('/v1/admin/logs/performance/connections');
    return response.data;
  },

  // Per-frame WebSocket envelope log (from comms_envelopes). Populated only when
  // COMMS_ENVELOPE_LOG=on. Returns { enabled, summary[], recent[] }. Bodies are
  // never stored — this is message metadata only (type/direction/bytes).
  async getPerformanceEnvelopes({ hours, from, to, observatory, type, direction, limit } = {}) {
    const response = await api.get('/v1/admin/logs/performance/envelopes', {
      params: { hours, from, to, observatory, type, direction, limit }
    });
    return response.data;
  },

  // API endpoint calls within an explicit [from,to] window (ISO strings) — backs the
  // Container-CPU drill-down. Returns { from, to, totals, topEndpoints[], heaviest[] }.
  async getPerformanceEndpointsWindow({ from, to, limit } = {}) {
    const response = await api.get('/v1/admin/logs/performance/endpoints', {
      params: { from, to, limit }
    });
    return response.data;
  },

  // Fire-and-forget SPA page-view beacon — one per client-side route change. Server stores it only
  // when PAGE_VIEW_TRACKING=on; failures are swallowed so navigation is never affected.
  recordPageView({ route, from } = {}) {
    return api.post('/v1/admin/logs/page-view', { route, from }).catch(() => {});
  },

  // Top SPA pages over the window (from page_views). Populated only when PAGE_VIEW_TRACKING=on.
  // Returns { from, to, totals, pages[] }. Accepts now-relative `hours` or explicit `from`/`to`.
  async getPageViews({ hours, from, to, limit } = {}) {
    const response = await api.get('/v1/admin/logs/page-views', {
      params: { hours, from, to, limit }
    });
    return response.data;
  },

  // User management (if auth routes become available)
  async getUsers(params = {}) {
    const response = await api.get('/v1/users', { params });
    return response.data;
  },

  async createUser(data) {
    const response = await api.post('/v1/users', data);
    return response.data;
  },

  async updateUser(id, data) {
    const response = await api.put(`/v1/users/${id}`, data);
    return response.data;
  },

  async deleteUser(id) {
    const response = await api.delete(`/v1/users/${id}`);
    return response.data;
  },

  // Send an email to a set of selected users via the server (Mailgun).
  // The server resolves addresses from the IDs; raw emails are never sent.
  async emailUsers({ userIds, subject, message }) {
    const response = await api.post('/v1/users/email', { userIds, subject, message });
    return response.data;
  },

  async getUser(id) {
    const response = await api.get(`/v1/users/${id}`);
    return response.data;
  },

  // The user's own usage-report schedule + last generated report (download).
  async updateUserReportSettings(id, reportSettings) {
    const response = await api.put(`/v1/users/${id}/report-settings`, { report_settings: reportSettings });
    return response.data;
  },

  async downloadUserReport(id) {
    const response = await api.get(`/v1/users/${id}/report-download`);
    return response.data;
  },

  async resetUserPassword(id, newPassword) {
    const response = await api.put(`/v1/users/${id}/reset-password`, {
      new_password: newPassword
    });
    return response.data;
  },

  async getUserMemberships(id) {
    const response = await api.get(`/v1/users/${id}/memberships`);
    return response.data;
  },

  async getUserLoginHistory(userId, limit = 50) {
    const response = await api.get(`/v1/userActivity/users/${userId}`, {
      params: { event_type: 'login,login_failed', limit }
    });
    return response.data;
  },

  // Subscriptions & membership tiers (doc §16)
  async getSubscriptionPlans(ladder) {
    const params = ladder ? { ladder } : {};
    const response = await api.get('/v1/subscriptions/plans', { params });
    return response.data;
  },

  async getMySubscription() {
    const response = await api.get('/v1/subscriptions/me');
    return response.data;
  },

  async getMyProjectLimit() {
    const response = await api.get('/v1/subscriptions/me/project-limit');
    return response.data;
  },

  // S3 storage usage grouped by project (for the My Projects storage display)
  async getFileStorageByProject() {
    const response = await api.get('/v1/user-files/by-project');
    return response.data;
  },

  // S3 storage usage grouped by observation (per-target / per-observation rows)
  async getFileStorageByObservation() {
    const response = await api.get('/v1/user-files/by-observation');
    return response.data;
  },

  // Bulk-delete user files by id. The server deletes each file's storage
  // objects + DB record sequentially, so one big request blows past the
  // gateway's upstream-read timeout (observed: nginx 504 at ~180s) and the
  // batch gets retried, double-deleting. Send small sequential chunks instead:
  // every request stays short, failures are isolated per chunk, and the
  // deleted/failed counts are aggregated into the server's response shape.
  async bulkDeleteFiles(fileIds) {
    const ids = Array.isArray(fileIds) ? fileIds : [];
    const CHUNK_SIZE = 50;
    const agg = { deleted: 0, failed: 0, errors: [] };
    for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
      const slice = ids.slice(i, i + CHUNK_SIZE);
      try {
        const response = await api.delete('/v1/user-files', { data: { fileIds: slice }, timeout: 120000 });
        const data = response.data?.data || response.data || {};
        agg.deleted += data.deleted || 0;
        agg.failed += data.failed || 0;
        if (Array.isArray(data.errors)) agg.errors.push(...data.errors);
      } catch (err) {
        // Isolate the chunk: count its files as failed and keep going so one
        // bad batch doesn't abort the whole delete.
        agg.failed += slice.length;
        agg.errors.push(err?.response?.data?.message || err.message || 'request failed');
      }
    }
    return { success: agg.failed === 0, data: agg };
  },

  // Pre-check: can the caller (as an operator) add another user, or is the max_users cap met?
  // Returns { block, message, limit, used } — block:true (enforce mode only) means at limit.
  async getMyUserCap() {
    const response = await api.get('/v1/subscriptions/me/user-cap');
    return response.data;
  },

  // Pre-check: can the caller create another PERSONAL project, or is max_projects met?
  // Returns { block, message, limit, used, subject_type, subject_name }.
  async getMyProjectCap() {
    const response = await api.get('/v1/subscriptions/me/project-cap');
    return response.data;
  },

  // The caller's own resolved effective entitlements + user subscription.
  // Returns { entitlements: { plan_key, display_name, source, is_default, entitlements: {...} }, subscription }.
  async getMyEntitlements() {
    const response = await api.get('/v1/subscriptions/me');
    return response.data;
  },

  // An observatory's resolved subscription tier for display (own, else inherited from owner org).
  // Returns { plan_key, display_name, description, status, inherited, source, source_name, entitlements }.
  async getObservatorySubscriptionView(observatoryId) {
    const response = await api.get(`/v1/subscriptions/observatory/${observatoryId}/view`);
    return response.data;
  },

  // Server-admin report: for each entity of subjectType (user|organization|observatory), how it gets
  // its subscription — direct/inherited, the kind, and the hierarchy path; multiple sources listed.
  async getSubscriptionSourceReport(subjectType) {
    const response = await api.get('/v1/subscriptions/report', { params: { subject_type: subjectType } });
    return response.data;
  },

  // Summary: count of subscriptions per tier. Rows: [{ plan_key, display_name, count }].
  async getSubscriptionSummary() {
    const response = await api.get('/v1/subscriptions/report/summary');
    return response.data;
  },

  // Entities directly assigned a given tier (click-through from the summary).
  async getSubscriptionEntitiesByPlan(planKey) {
    const response = await api.get('/v1/subscriptions/report/entities', { params: { plan_key: planKey } });
    return response.data;
  },

  // Server-admin "View as user": mint a short-lived (30 min), read-only token
  // for the target user. Returns { token, expiresIn, read_only, user }.
  async impersonateUser(userId) {
    const response = await api.post(`/v1/users/${userId}/impersonate`);
    return response.data;
  },

  // Usage Billing tab: every tier (including empty ones) with its active holders.
  async getBillingOverview() {
    const response = await api.get('/v1/subscriptions/billing/overview');
    return response.data;
  },

  // Usage Billing tab: on-demand compact usage-vs-limits reports for the given holders
  // ([{ subject_type, id }]); empty/omitted → all active holders.
  async generateBillingReports(subjects) {
    const response = await api.post('/v1/subscriptions/billing/report', { subjects: subjects || [] });
    return response.data;
  },

  // Audit Usage tab: every active holder, tier-ordered, with each applicable limit set against its
  // actual and a verdict (ok / near / over). Same builder as the Usage Billing download.
  async getUsageAudit() {
    const response = await api.get('/v1/subscriptions/audit/usage');
    return response.data;
  },

  // Audit Usage tab: mail the audit. No recipients → the requesting admin's own address.
  async emailUsageAudit({ recipients = [], note = '', onlyExceptions = false } = {}) {
    const response = await api.post('/v1/subscriptions/audit/email', {
      recipients, note, only_exceptions: onlyExceptions,
    });
    return response.data;
  },

  // A subject's effective entitlements (override-aware limits) + current actuals (entity drill-down).
  async getSubscriptionUsageView(subjectType, subjectId) {
    const response = await api.get(`/v1/subscriptions/${subjectType}/${subjectId}/usage-view`);
    return response.data;
  },

  // Full detailed report (every active entity → tier + limits + actuals) for CSV export.
  async getSubscriptionReportExport() {
    const response = await api.get('/v1/subscriptions/report/export');
    return response.data;
  },

  // Bulk reads (server admin) for the Service Center tables
  async listSubscriptions(subjectType) {
    const params = subjectType ? { subject_type: subjectType } : {};
    const response = await api.get('/v1/subscriptions', { params });
    return response.data;
  },

  async listObservatoryClassifications() {
    const response = await api.get('/v1/subscriptions/classifications');
    return response.data;
  },

  async getSubjectSubscription(subjectType, subjectId) {
    const response = await api.get(`/v1/subscriptions/${subjectType}/${subjectId}`);
    return response.data;
  },

  async assignSubscription(data) {
    const response = await api.post('/v1/subscriptions', data);
    return response.data;
  },

  async updateSubjectSubscription(subjectType, subjectId, data) {
    const response = await api.put(`/v1/subscriptions/${subjectType}/${subjectId}`, data);
    return response.data;
  },

  async cancelSubjectSubscription(subjectType, subjectId) {
    const response = await api.delete(`/v1/subscriptions/${subjectType}/${subjectId}`);
    return response.data;
  },

  async getObservatoryClassification(observatoryId) {
    const response = await api.get(`/v1/subscriptions/classifications/${observatoryId}`);
    return response.data;
  },

  async putObservatoryClassification(observatoryId, data) {
    const response = await api.put(`/v1/subscriptions/classifications/${observatoryId}`, data);
    return response.data;
  },

  // Organization management
  async getOrganizations() {
    const response = await api.get('/v1/organizations');
    return response.data;
  },

  async getOrganization(id) {
    const response = await api.get(`/v1/organizations/${id}`);
    return response.data;
  },

  // Organization audit-trail history (mirrors getProjectHistory)
  async getOrganizationHistory(id, params = {}) {
    const response = await api.get(`/v1/organizations/${id}/history`, { params });
    return response.data;
  },

  async createOrganization(data) {
    const response = await api.post('/v1/organizations', data);
    return response.data;
  },

  async updateOrganization(id, data) {
    const response = await api.put(`/v1/organizations/${id}`, data);
    return response.data;
  },

  async deleteOrganization(id) {
    const response = await api.delete(`/v1/organizations/${id}`);
    return response.data;
  },

  // Organization member management
  async getOrganizationMembers(orgId) {
    const response = await api.get(`/v1/organizations/${orgId}/members`);
    return response.data;
  },

  async addOrganizationMember(orgId, data) {
    const response = await api.post(`/v1/organizations/${orgId}/members`, data);
    return response.data;
  },

  async updateOrganizationMemberPermissions(orgId, userId, permissions) {
    const response = await api.put(`/v1/organizations/${orgId}/members/${userId}`, { permissions });
    return response.data;
  },

  async removeOrganizationMember(orgId, userId) {
    const response = await api.delete(`/v1/organizations/${orgId}/members/${userId}`);
    return response.data;
  },

  // Organization member-organization (nested org) management
  async addMemberOrganization(orgId, data) {
    const response = await api.post(`/v1/organizations/${orgId}/member-organizations`, data);
    return response.data;
  },

  async updateMemberOrganization(orgId, childId, permissions) {
    const response = await api.put(`/v1/organizations/${orgId}/member-organizations/${childId}`, { permissions });
    return response.data;
  },

  async removeMemberOrganization(orgId, childId) {
    const response = await api.delete(`/v1/organizations/${orgId}/member-organizations/${childId}`);
    return response.data;
  },

  // Child-centric: nest THIS org (orgId) under a new parent org, detaching from any current parent.
  // transferOwnershipToParent=true also hands this org's ownership to the parent org's owner user.
  async moveOrganizationToParent(orgId, newParentId, permissions, transferOwnershipToParent = false) {
    const response = await api.post(`/v1/organizations/${orgId}/move-to-parent`, {
      new_parent_id: newParentId,
      transfer_ownership_to_parent: !!transferOwnershipToParent,
      ...(permissions ? { permissions } : {})
    });
    return response.data;
  },

  async sendEmail({ to, subject, message, replyTo }) {
    const response = await api.post('/v1/send-email', { to, subject, message, ...(replyTo && { replyTo }) });
    return response.data;
  },

  async transferOrganizationOwnership(orgId, newOwnerId) {
    const response = await api.post(`/v1/organizations/${orgId}/transfer-ownership`, { new_owner_id: newOwnerId });
    return response.data;
  },

  // Get organization memberships for a specific user (admin only)
  async getUserOrganizationMemberships(userId) {
    const response = await api.get(`/v1/organizations/users/${userId}/organizations`);
    return response.data;
  },

  // Get project memberships for a specific user (admin only)
  async getUserProjectMemberships(userId) {
    const response = await api.get(`/v1/projects/users/${userId}/projects`);
    return response.data;
  },

  // The fully resolved access tree for a user (#1212): organizations, observatories and projects
  // they can reach, each with its route reason and effective permissions. Resolved server-side from
  // the canonical access-control helpers — deliberately NOT re-derived in the browser from the
  // paginated list endpoints, which is what silently hid org-owned projects.
  async getUserAccessTree(userId) {
    const response = await api.get(`/v1/users/${userId}/access-tree`);
    return response.data;
  },

  // A user's effective observatory permissions (direct + org/nested-org membership)
  async getUserEffectiveObservatoryPermissions(observatoryId, userId) {
    const response = await api.get(`/v1/observatories/${observatoryId}/users/${userId}/effective-permissions`);
    return response.data;
  },

  // A user's effective (direct + nested-org) permissions on an organization
  async getUserEffectiveOrgPermissions(orgId, userId) {
    const response = await api.get(`/v1/organizations/${orgId}/users/${userId}/effective-permissions`);
    return response.data;
  },

  // A user's effective project permissions (direct + org/observatory inheritance)
  async getUserEffectiveProjectPermissions(projectId, userId) {
    const response = await api.get(`/v1/projects/${projectId}/users/${userId}/effective-permissions`);
    return response.data;
  },

  // MOTD (Message of the Day) methods
  async createMotd(observatoryId, motdData) {
    const response = await api.post(`/v1/observatories/${observatoryId}/motd`, motdData);
    return response.data;
  },

  async getActiveMotds(observatoryId) {
    const response = await api.get(`/v1/observatories/${observatoryId}/motd`);
    return response.data;
  },

  async getMotdHistory(observatoryId) {
    const response = await api.get(`/v1/observatories/${observatoryId}/motd/history`);
    return response.data;
  },

  async deactivateMotd(observatoryId, motdId, reason) {
    const response = await api.delete(`/v1/observatories/${observatoryId}/motd/${motdId}`, {
      data: reason ? { reason } : undefined
    });
    return response.data;
  },

  async acknowledgeMotd(observatoryId, motdId) {
    const response = await api.post(`/v1/observatories/${observatoryId}/motd/${motdId}/acknowledge`);
    return response.data;
  },

  async getObservatoryMotdRecipients(observatoryId, motdId) {
    const response = await api.get(`/v1/observatories/${observatoryId}/motd/${motdId}/recipients`);
    return response.data;
  },

  async getPendingMotds() {
    const response = await api.get('/v1/user/motd/pending');
    return response.data;
  },

  // System MOTD methods
  async createSystemMotd(motdData) {
    const response = await api.post('/v1/system/motd', motdData);
    return response.data;
  },

  async getSystemMotds(includeInactive = false) {
    const response = await api.get(`/v1/system/motd${includeInactive ? '?include_inactive=true' : ''}`);
    return response.data;
  },

  async deactivateSystemMotd(motdId, reason) {
    const response = await api.delete(`/v1/system/motd/${motdId}`, {
      data: reason ? { reason } : undefined
    });
    return response.data;
  },

  async acknowledgeSystemMotd(motdId) {
    const response = await api.post(`/v1/system/motd/${motdId}/acknowledge`);
    return response.data;
  },

  async getSystemMotdRecipients(motdId) {
    const response = await api.get(`/v1/system/motd/${motdId}/recipients`);
    return response.data;
  },

  // Legacy alias for backward compatibility
  async getInstitutions() {
    return this.getOrganizations();
  },

  // Observation search
  async searchObservations(params = {}) {
    const response = await api.get('/v1/observations/search', { params });
    return response.data;
  },

  // Target observation report
  async getTargetReport(params = {}) {
    const response = await api.get('/v1/reporting/observations/by-target', { params });
    return response.data;
  },

  async getTargetReportDetail(params = {}) {
    const response = await api.get('/v1/reporting/observations/by-target/detail', { params });
    return response.data;
  },

  // Observation management
  async getObservations(params = {}) {
    const response = await api.get('/v1/observations', { params });
    return response.data;
  },

  async getObservation(id) {
    const response = await api.get(`/v1/observations/${id}`);
    return response.data;
  },

  /**
   * Pre-submit duration estimate (#937/#938). Returns { estimated_minutes, breakdown,
   * grace_minutes, derived_window_minutes } — the same number the scheduler will use, so the
   * setup form can show how long an observation actually takes before anything is persisted.
   */
  async estimateObservationDuration({ targets, assignedTo, globalRepeatCount, autofocusOptions }) {
    const response = await api.post('/v1/observations/estimate-duration', {
      targets,
      assigned_to: assignedTo,
      global_repeat_count: globalRepeatCount,
      autofocus_options: autofocusOptions,
    });
    return response.data?.data;
  },

  async createObservation(data) {
    try {
      const response = await api.post('/v1/observations', normalizeObservationPayload(data));
      return response.data;
    } catch (error) {
      // Handle 409 Conflict with adjustment options - return as result instead of throwing
      if (error.response?.status === 409 && error.response?.data?.error?.adjustment_options) {
        console.log('Observation conflict with adjustment options:', error.response.data);
        return error.response.data;  // Return as { success: false, error: { adjustment_options: [...] } }
      }
      throw error;
    }
  },

  async createRepetitiveObservation(observationData, repetitionConfig) {
    const response = await api.post('/v1/observations/repetitive', {
      observation_data: normalizeObservationPayload(observationData),
      repetition_config: repetitionConfig
    });
    return response.data;
  },

  async getUpcomingRepetitive(days = 7, observatoryId = null) {
    const params = { days };
    if (observatoryId) {
      params.observatory_id = observatoryId;
    }
    const response = await api.get('/v1/observations/repetitive/upcoming', { params });
    return response.data;
  },

  async getExecutionHistory(observationId) {
    const response = await api.get(`/v1/observations/${observationId}/executions`);
    return response.data;
  },

  async pauseRepetitiveObservation(observationId) {
    const response = await api.put(`/v1/observations/${observationId}/repetition-control`, {
      action: 'pause'
    });
    return response.data;
  },

  async resumeRepetitiveObservation(observationId) {
    const response = await api.put(`/v1/observations/${observationId}/repetition-control`, {
      action: 'resume'
    });
    return response.data;
  },

  async updateRepetitionConfig(observationId, repetitionConfig) {
    const response = await api.put(`/v1/observations/${observationId}/repetition-config`, {
      repetition_config: repetitionConfig
    });
    return response.data;
  },

  async getRepetitiveStats() {
    const response = await api.get('/v1/observations/repetitive/stats');
    return response.data;
  },

  async updateObservation(id, data) {
    const response = await api.put(`/v1/observations/${id}`, normalizeObservationPayload(data));
    return response.data;
  },

  async deleteObservation(id, deleteFiles = false) {
    console.log('apiService.deleteObservation called with ID:', id, 'deleteFiles:', deleteFiles);
    const url = `/v1/observations/${id}${deleteFiles ? '?delete_files=true' : ''}`;
    const response = await api.delete(url);
    return response.data;
  },

  async bulkDeleteObservations(observationIds, deleteFiles = false) {
    console.log('apiService.bulkDeleteObservations called with IDs:', observationIds, 'deleteFiles:', deleteFiles);
    try {
      const response = await api.post('/v1/observations/bulk-delete', {
        observation_ids: observationIds,
        delete_files: deleteFiles
      });
      console.log('Bulk delete response:', response);
      return response.data;
    } catch (error) {
      console.error('Bulk delete API error:', error);
      console.error('Error response:', error.response);
      throw error;
    }
  },

  async getDispatchStatus(observatoryId = null, status = 'pending') {
    const params = { status };
    if (observatoryId) {
      params.observatory_id = observatoryId;
    }
    const response = await api.get('/v1/observations/dispatch-status', { params });
    return response.data;
  },

  async updateObservationStatus(id, status, reason = null, data = null) {
    const response = await api.post(`/v1/observations/${id}/status`, {
      status,
      reason,
      data
    });
    return response.data;
  },

  async getFileDownloadUrl(observationId, fileId) {
    const response = await api.get(`/v1/observations/${observationId}/files/${fileId}/download`);
    return response.data;
  },

  // Get download URL for a user file (from UserFile collection)
  async getUserFileDownloadUrl(fileId) {
    const response = await api.get(`/v1/user-files/download-url/${fileId}`);
    return response.data;
  },

  // Get heavy per-file detail (fits_header/quality_metrics/plate_solve_result).
  // The observation file-list endpoint is now lean; the file-detail modals fetch
  // this on open and merge it into the selected file.
  async getUserFile(observationId, fileId) {
    const response = await api.get(`/v1/user-files/observation/${observationId}/file/${fileId}`);
    return response.data; // { success, data: { file_id, fits_header, quality_metrics, plate_solve_result } }
  },

  // Get files for a specific observation.
  //
  // Fetches in bounded pages and concatenates (#851). Previously this made a
  // single unpaginated request; for a large backlog the server had to map the
  // entire file set into one response, which — on the memory-constrained prod
  // box — triggered a native-allocation SIGSEGV. Paging caps each request at
  // `pageSize` files while callers still receive the full list (return shape
  // unchanged: { success, data: { ...meta, files } }), so the rebuild/download/
  // totals logic that depends on the complete list keeps working.
  async getUserFiles({ observationId, pageSize = 250, maxFiles = 10000 }) {
    let page = 1;
    let allFiles = [];
    let last = null;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const response = await api.get(`/v1/user-files/observation/${observationId}`, {
        params: { page, limit: pageSize },
      });
      last = response.data;
      const files = last?.data?.files || [];
      allFiles = allFiles.concat(files);
      const totalPages = last?.data?.pagination?.totalPages || 1;
      if (page >= totalPages || files.length === 0 || allFiles.length >= maxFiles) break;
      page += 1;
    }
    return last ? { ...last, data: { ...last.data, files: allFiles } } : { success: false, data: { files: [] } };
  },

  // Observation log methods
  async getObservationLogs(observationId, params = {}) {
    const response = await api.get(`/v1/observations/${observationId}/logs`, { params });
    return response.data;
  },

  async getObservationLogSummary(observationId) {
    const response = await api.get(`/v1/observations/${observationId}/logs/summary`);
    return response.data;
  },

  async getObservationTimeline(observationId) {
    const response = await api.get(`/v1/observations/${observationId}/logs/timeline`);
    return response.data;
  },

  async addObservationNote(observationId, note) {
    const response = await api.post(`/v1/observations/${observationId}/logs/note`, { note });
    return response.data;
  },

  // Multi-day timeline produced by the Python scheduler — same payload
  // the operator's TimelineView / ObservationTimeline / MultiDayTimeline
  // already consume. Used by ScheduleAnalyticsPane to render every
  // accessible observatory's tonight plan stacked on a shared axis.
  async getScheduleTimeline(observatoryId, { days = 1 } = {}) {
    const response = await api.get('/v1/scheduler/multi-day-timeline', {
      params: { observatory_id: observatoryId, days },
    });
    return response.data;
  },

  // Per-observatory pipeline performance averages over a rolling window.
  // Used by the PerformanceStatsCard on the operations dashboard. Window
  // values supported by the server: '7d', '30d', '90d'.
  async getPerformanceStats(observatoryId, { window = '30d' } = {}) {
    const response = await api.get(
      `/v1/observatories/${observatoryId}/performance-stats`,
      { params: { window } },
    );
    return response.data;
  },

  // Per-observatory pipeline interval time series for the expanded chart
  // grid. Returns one array of points per metric, sorted ascending by
  // completed_at. Server caps at 200 observations to keep the payload
  // bounded.
  async getPerformanceStatsSeries(observatoryId, { window = '30d' } = {}) {
    const response = await api.get(
      `/v1/observatories/${observatoryId}/performance-stats/series`,
      { params: { window } },
    );
    return response.data;
  },

  // Observatory Quality Score — realized-performance score (0–100) with a
  // per-pillar breakdown over a rolling window. This is distinct from the
  // commercial ObservatoryClassification (class A/B/C). Window: '7d'|'30d'|'90d'.
  async getObservatoryQualityScore(observatoryId, { window = '30d' } = {}) {
    const response = await api.get(
      `/v1/observatories/${observatoryId}/quality-score`,
      { params: { window } },
    );
    return response.data;
  },

  // Cross-observatory Quality Score leaderboard (server-admin only).
  async getObservatoryQualityRanking({ window = '30d' } = {}) {
    const response = await api.get(
      `/v1/observatories/quality-score/ranking`,
      { params: { window } },
    );
    return response.data;
  },

  // Quality Score history (hourly snapshots) for charting trends over time.
  // Returns an ordered series of { snapshot_at, composite_score, pillars, data_confidence_score }.
  async getObservatoryQualityHistory(observatoryId, { window = '30d', days = 30 } = {}) {
    const response = await api.get(
      `/v1/observatories/${observatoryId}/quality-score/history`,
      { params: { window, days } },
    );
    return response.data;
  },

  // Reassign an observation to a different project. Server validates
  // permissions, observation state (terminal only), and target project
  // accessibility; emits a project_moved entry on the observation log.
  async moveObservationToProject(observationId, target_project_id) {
    const response = await api.post(
      `/v1/observations/${observationId}/move-project`,
      { target_project_id }
    );
    return response.data;
  },

  // Real-time status
  async getConnectedObservatories() {
    const response = await api.get('/v1/observatories/connected');
    return response.data;
  },

  async getActiveObservations() {
    const response = await api.get('/v1/observations/active');
    return response.data;
  },

  // System management
  async clearMessageQueue() {
    const response = await api.delete('/v1/system/message-queue');
    return response.data.data;
  },

  async resetTestData() {
    const response = await api.post('/v1/system/reset-test-data', {
      confirm: 'RESET_ALL_DATA'
    });
    return response.data.data;
  },

  // Project management
  async getProjects(params = {}) {
    const response = await api.get('/v1/projects', { params });
    return response.data;
  },

  // Fetch the COMPLETE project list, walking every page. GET /v1/projects
  // defaults to 20 rows and hard-caps `limit` at 100 server-side, so a picker
  // that must surface every project a caller could assign (e.g. the Add/Edit
  // User membership form, where a server-admin sees all projects) cannot rely
  // on a single request — anything past the first page silently disappears.
  // Reads page 1 to learn the page count, fetches the rest in parallel, and
  // de-dupes by _id in case a project shifts pages between requests (the list
  // is sorted by updated_at). Returns a flat array of project objects with
  // `owner_id` populated — NOT the paginated { data: { projects, pagination } }
  // envelope that getProjects() returns.
  async getAllProjects(params = {}) {
    const PAGE_SIZE = 100;
    const unwrap = (res) =>
      res?.data?.projects || (Array.isArray(res?.data) ? res.data : []);

    const first = await this.getProjects({ ...params, page: 1, limit: PAGE_SIZE });
    const firstPage = unwrap(first);
    const totalPages = first?.data?.pagination?.pages || 1;

    let all = firstPage;
    if (totalPages > 1) {
      const rest = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          this.getProjects({ ...params, page: i + 2, limit: PAGE_SIZE })
            .then(unwrap)
            .catch(() => [])
        )
      );
      all = firstPage.concat(...rest);
    }

    const seen = new Set();
    const deduped = [];
    for (const project of all) {
      const id = project && project._id != null ? String(project._id) : '';
      if (id && seen.has(id)) continue;
      if (id) seen.add(id);
      deduped.push(project);
    }
    return deduped;
  },

  // User API keys (#1115): programmatic access via HTTP Basic, in place of storing a password.
  // The secret is returned by createMyApiKey ONCE and is unrecoverable afterwards — the server
  // stores only an HMAC of it.
  async getMyApiKeys() {
    const response = await api.get('/v1/users/me/api-keys');
    return response.data;
  },

  async createMyApiKey(data) {
    const response = await api.post('/v1/users/me/api-keys', data);
    return response.data;
  },

  async renameMyApiKey(id, name) {
    const response = await api.patch(`/v1/users/me/api-keys/${id}`, { name });
    return response.data;
  },

  async revokeMyApiKey(id) {
    const response = await api.delete(`/v1/users/me/api-keys/${id}`);
    return response.data;
  },

  async getProject(id) {
    const response = await api.get(`/v1/projects/${id}`);
    return response.data;
  },

  // Owners the current user may create a project under (#1177): Personal plus every
  // organization they belong to — child orgs included — each flagged with whether a
  // subscription backs it and whether it may be selected under the current
  // SUBSCRIPTION_ENFORCEMENT mode.
  async getEligibleProjectOwners() {
    const response = await api.get('/v1/projects/eligible-owners');
    return response.data;
  },

  // Project audit-trail history (issue #883)
  async getProjectHistory(id, params = {}) {
    const response = await api.get(`/v1/projects/${id}/history`, { params });
    return response.data;
  },

  async createProject(data) {
    const response = await api.post('/v1/projects', data);
    return response.data;
  },

  async updateProject(id, data) {
    const response = await api.patch(`/v1/projects/${id}`, data);
    return response.data;
  },

  async deleteProject(id, force = false) {
    const response = await api.delete(`/v1/projects/${id}${force ? '?force=true' : ''}`);
    return response.data;
  },

  async addProjectMember(projectId, data) {
    const response = await api.post(`/v1/projects/${projectId}/members`, data);
    return response.data;
  },

  async removeProjectMember(projectId, userId) {
    const response = await api.delete(`/v1/projects/${projectId}/members/${userId}`);
    return response.data;
  },

  // Grant an organization access to a project (org-level share)
  async addProjectOrganization(projectId, organizationId, permissions) {
    const response = await api.post(`/v1/projects/${projectId}/members/organization`, {
      organization_id: organizationId,
      permissions
    });
    return response.data;
  },

  async updateProjectOrganizationPermissions(projectId, orgId, permissions) {
    const response = await api.put(`/v1/projects/${projectId}/members/organization/${orgId}`, { permissions });
    return response.data;
  },

  async removeProjectOrganization(projectId, orgId) {
    const response = await api.delete(`/v1/projects/${projectId}/members/organization/${orgId}`);
    return response.data;
  },

  async getProjectObservations(projectId, params = {}) {
    const response = await api.get(`/v1/projects/${projectId}/observations`, { params });
    return response.data;
  },

  async updateProjectStats(projectId) {
    const response = await api.post(`/v1/projects/${projectId}/update-stats`);
    return response.data;
  },

  // Get project members
  async getProjectMembers(projectId) {
    const response = await api.get(`/v1/projects/${projectId}/members`);
    return response.data;
  },

  // Update project member permissions
  async updateProjectMemberPermissions(projectId, userId, permissions) {
    const response = await api.patch(`/v1/projects/${projectId}/members/${userId}`, { permissions });
    return response.data;
  },

  // Transfer project ownership
  async transferProjectOwnership(projectId, toOwnerType, toOwnerId, reason = null) {
    const response = await api.post(`/v1/projects/${projectId}/transfer`, {
      to_owner_type: toOwnerType,
      to_owner_id: toOwnerId,
      reason
    });
    return response.data;
  },

  // Coordinate lookup. The server caps a miss at ~10s (LOOKUP_DEADLINE_MS) and then
  // returns a descriptive not-found; use a longer client timeout than the 10s api
  // default so we receive that message instead of tripping our own timeout first.
  async lookupCoordinates(objectName) {
    const response = await api.get(
      `/v1/coordinates/lookup?objectName=${encodeURIComponent(objectName)}`,
      { timeout: 15000 }
    );
    return response.data;
  },

  // Profile management
  async getProfile() {
    const response = await api.get('/v1/users/profile');
    return response.data;
  },

  async updateProfile(data) {
    const response = await api.put('/v1/users/profile', data);
    return response.data;
  },

  async saveNotificationPreferences(notifications) {
    const response = await api.put('/v1/users/profile/notifications', notifications);
    return response.data;
  },

  async testNotifications(channels) {
    const response = await api.post('/v1/users/profile/notifications/test', channels);
    return response.data;
  },

  async uploadAvatar(file) {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await api.post('/v1/users/profile/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000,
    });
    return response.data;
  },

  async deleteAvatar() {
    const response = await api.delete('/v1/users/profile/avatar');
    return response.data;
  },

  async updatePreferences(preferences) {
    // Read-only "View as user": an impersonation token is rejected (403) on any
    // non-allow-listed mutation, and PUT /users/profile isn't allow-listed. These
    // preference writes are convenience side-effects (e.g. last_observatory_id set
    // by the observation submit form), often fire-and-forget — under impersonation
    // they'd surface as an unhandled 403 rejection, and we must not overwrite the
    // impersonated user's saved prefs with the admin's transient choices anyway.
    // No-op the server write; local UI state is updated by the caller.
    if (getImpersonation()) {
      return { success: true, skipped: 'impersonation' };
    }
    // First fetch current user to get existing preferences, then merge and send whole object
    // This avoids Mongoose/Mixed field issues with dot notation $set
    try {
      const meResponse = await api.get('/v1/auth/me');
      const currentPrefs = meResponse.data?.data?.preferences || {};
      const merged = { ...currentPrefs, ...preferences };
      // Remove null values (treat null as "unset")
      for (const key of Object.keys(merged)) {
        if (merged[key] === null) delete merged[key];
      }
      const response = await api.put('/v1/users/profile', { preferences: merged });
      return response.data;
    } catch (err) {
      // Fallback to dot notation if /me fails
      const dotNotation = {};
      for (const [key, value] of Object.entries(preferences)) {
        dotNotation[`preferences.${key}`] = value;
      }
      const response = await api.put('/v1/users/profile', dotNotation);
      return response.data;
    }
  },

  async getUserOrganizations() {
    const response = await api.get('/v1/organizations/users/me/organizations');
    return response.data;
  },

  // Legacy alias for backward compatibility
  async getUserInstitutions() {
    return this.getUserOrganizations();
  },

  // Get latest image from observatory via dedicated server endpoint
  async getObservatoryLatestImage(id) {
    try {
      const response = await api.get(`/v1/observatories/${id}/latest-image`);
      return response.data?.data || null;
    } catch (error) {
      console.error('Failed to get latest image:', error);
      return null;
    }
  },

  // Exoplanet transit methods
  async getExoplanetCacheStatus() {
    const response = await api.get('/v1/exoplanets/cache-status');
    return response.data;
  },

  async updateExoplanetCache() {
    const response = await api.post('/v1/exoplanets/update-cache');
    return response.data;
  },

  async clearAndRefreshExoplanetCache() {
    const response = await api.post('/v1/exoplanets/cache/clear-and-refresh');
    return response.data;
  },

  async getUpcomingTransits(params) {
    const response = await api.get('/v1/exoplanets/upcoming-transits', { params });
    return response.data;
  },

  async generateExoplanetPlan(data) {
    const response = await api.post('/v1/exoplanets/generate-plan', data);
    return response.data;
  },

  async searchExoplanets(query) {
    const response = await api.get('/v1/exoplanets/search', {
      params: { q: query }
    });
    return response.data;
  },

  async getExoplanetDetails(name) {
    const response = await api.get(`/v1/exoplanets/${name}`);
    return response.data;
  },

  // Deep Sky catalog methods
  async getDeepSkyCacheStatus() {
    const response = await api.get('/v1/deepsky/cache-status');
    return response.data;
  },

  async updateDeepSkyCache() {
    const response = await api.post('/v1/deepsky/update-cache');
    return response.data;
  },

  async getVisibleDeepSkyObjects(params) {
    const response = await api.get('/v1/deepsky/visible-tonight', { params });
    return response.data;
  },

  // Admin integrity / backup stats — wrapper so callers go through the
  // axios instance that attaches the Bearer token. Raw fetch() calls
  // were 401-ing because the request interceptor only runs for
  // requests issued through `api`.
  async getBackupStats() {
    const response = await api.get('/v1/admin/integrity/backups/stats');
    return response.data;
  },

  // MinIO storage stats — server-admin only (requireAuth + requireAdmin).
  // Must go through `api` so the Bearer token is attached; a raw fetch()
  // 401s because the auth interceptor only runs for `api` requests.
  async getMinioStats() {
    const response = await api.get('/v1/minio/stats');
    return response.data;
  },

  // Queue depths, upload backpressure, and ingest/processing rates for the
  // System Status page — server-admin only. Through `api` for the Bearer token.
  async getThroughputMetrics() {
    const response = await api.get('/v1/metrics/throughput');
    return response.data;
  },

  // Node diagnostic crash reports — server-admin only (#1184).
  async getDiagnosticReports() {
    const response = await api.get('/v1/system/diagnostic-reports');
    return response.data;
  },

  // Raw JSON of one report as a Blob (for download). Goes through `api` for the token.
  async getDiagnosticReportBlob(filename) {
    const response = await api.get(`/v1/system/diagnostic-reports/${encodeURIComponent(filename)}/download`, { responseType: 'blob' });
    return response.data;
  },

  async searchDeepSkyObjects(query) {
    const response = await api.get('/v1/deepsky/search', { params: { q: query, limit: 20 } });
    return response.data;
  },

  async getDeepSkyObjectDetails(designation) {
    const response = await api.get(`/v1/deepsky/${encodeURIComponent(designation)}`);
    return response.data;
  },

  // Cancel a running observation
  async cancelObservation(observationId, reason = '') {
    const response = await api.post(`/v1/observations/${observationId}/cancel`, { reason });
    return response.data;
  },

  // System settings methods
  async getSystemSettings() {
    const response = await api.get('/v1/system-settings');
    return response.data;
  },

  async updateSystemSettings(settings) {
    const response = await api.put('/v1/system-settings', settings);
    return response.data;
  },

  // Library Targets (Target Library v2)
  async getLibraryTargets(params = {}) {
    const response = await api.get('/v1/library/targets', { params });
    return response.data;
  },

  async getLibraryTarget(id) {
    const response = await api.get(`/v1/library/targets/${id}`);
    return response.data;
  },

  async createLibraryTarget(data) {
    const response = await api.post('/v1/library/targets', data);
    return response.data;
  },

  async updateLibraryTarget(id, data) {
    const response = await api.put(`/v1/library/targets/${id}`, data);
    return response.data;
  },

  async deleteLibraryTarget(id) {
    const response = await api.delete(`/v1/library/targets/${id}`);
    return response.data;
  },

  async saveTargetsFromObservation(obsId, data) {
    const response = await api.post(`/v1/library/targets/from-observation/${obsId}`, data);
    return response.data;
  },

  // Library Templates (Target Library v2)
  async getLibraryTemplates(params = {}) {
    const response = await api.get('/v1/library/templates', { params });
    return response.data;
  },

  async getLibraryTemplate(id) {
    const response = await api.get(`/v1/library/templates/${id}`);
    return response.data;
  },

  async createLibraryTemplate(data) {
    const response = await api.post('/v1/library/templates', data);
    return response.data;
  },

  async updateLibraryTemplate(id, data) {
    const response = await api.put(`/v1/library/templates/${id}`, data);
    return response.data;
  },

  async deleteLibraryTemplate(id) {
    const response = await api.delete(`/v1/library/templates/${id}`);
    return response.data;
  },

  async activateLibraryTemplate(id) {
    const response = await api.post(`/v1/library/templates/${id}/activate`);
    return response.data;
  },

  async enableTemplateAutomation(id) {
    const response = await api.post(`/v1/library/templates/${id}/automation/enable`);
    return response.data;
  },

  async pauseTemplateAutomation(id) {
    const response = await api.post(`/v1/library/templates/${id}/automation/pause`);
    return response.data;
  },

  async disableTemplateAutomation(id) {
    const response = await api.post(`/v1/library/templates/${id}/automation/disable`);
    return response.data;
  },

  async getTemplateAutomationStatus(id) {
    const response = await api.get(`/v1/library/templates/${id}/automation/status`);
    return response.data;
  },

  async saveTemplateFromObservation(obsId, data) {
    const response = await api.post(`/v1/library/templates/from-observation/${obsId}`, data);
    return response.data;
  },

  // CSV Import (observation import from CSV target lists)
  async getCsvImportFormats() {
    const response = await api.get('/v1/csv-import/formats');
    return response.data;
  },

  async previewCsvImport(file, formatType = 'generic', previewRows = 5) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('formatType', formatType);
    formData.append('previewRows', String(previewRows));
    const response = await api.post('/v1/csv-import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return response.data;
  },

  async executeCsvImport(file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('formatType', options.formatType || 'generic');
    if (options.priority != null) formData.append('priority', String(options.priority));
    if (options.skipDuplicates != null) formData.append('skipDuplicates', String(options.skipDuplicates));
    if (options.addPrefix) formData.append('addPrefix', options.addPrefix);
    if (options.projectId) formData.append('projectId', options.projectId);
    const response = await api.post('/v1/csv-import/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return response.data;
  },

  async downloadCsvTemplate(formatType) {
    // responseType: 'blob' so the caller can trigger a browser download.
    // Goes through the axios instance for auth-header + token-refresh handling.
    const response = await api.get(`/v1/csv-import/template/${formatType}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * The observatory's dark-time ceiling for the night containing `anchor` (#1119) — the latest
   * instant an observation there may still be running.
   *
   * Returns `{ dawn_limit, twilight_type, respects_dark_time, observatory_name, ... }`, where
   * `dawn_limit: null` means "no ceiling" (the site opts out of dark-time constraints), NOT an
   * error. This is the same value computeRunUntil clamps against server-side, which is why the
   * browser asks for it instead of computing dawn itself.
   */
  async getObservatoryDarkTimeLimit(observatoryId, anchor = null) {
    const qs = anchor ? `?anchor=${encodeURIComponent(new Date(anchor).toISOString())}` : '';
    const response = await api.get(`/v1/observatories/${observatoryId}/dark-time-limit${qs}`);
    return response.data;
  },

  // Target Visibility (Python scheduler endpoint)
  async getTargetVisibility(targets, observatoryId, twilightType = 'astronomical') {
    const response = await api.post('/v1/target-visibility', {
      targets,
      observatory_id: observatoryId,
      twilight_type: twilightType
    });
    return response.data;
  },

  // Scheduler Timeline (Python scheduler endpoint)
  async getSchedulerTimeline(observatoryId, useCache = true) {
    const response = await api.get(`/v1/scheduler/timeline?observatory_id=${observatoryId}&use_cache=${useCache}`);
    return response.data;
  },

  // Health check
  async health() {
    const response = await api.get('/health');
    return response.data;
  },

  // Support requests (Issue #351)
  async submitSupportRequest(data) {
    const response = await api.post('/v1/support', data);
    return response.data;
  },

  async getSupportRequests() {
    const response = await api.get('/v1/support');
    return response.data;
  },

  // Support requests — admin (Service Center)
  async getAllSupportRequests(params = {}) {
    const response = await api.get('/v1/support/admin/all', { params });
    return response.data;
  },

  async replyToSupportRequest(id, data) {
    const response = await api.post(`/v1/support/${id}/reply`, data);
    return response.data;
  },

  async updateSupportRequestStatus(id, data) {
    const response = await api.put(`/v1/support/${id}/status`, data);
    return response.data;
  },

  // Direct axios instance for advanced usage
  get(url, config) {
    return api.get(url, config);
  },
  post(url, data, config) {
    return api.post(url, data, config);
  },
  put(url, data, config) {
    return api.put(url, data, config);
  },
  delete(url, config) {
    return api.delete(url, config);
  },
  patch(url, data, config) {
    return api.patch(url, data, config);
  },

  // Plugin Release Management
  async getPluginReleases() {
    const response = await api.get('/v1/plugin-releases');
    return response.data;
  },

  async uploadPluginRelease(formData, onUploadProgress) {
    const response = await api.post('/v1/plugin-releases', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000, // 5 min timeout for large uploads
      onUploadProgress
    });
    return response.data;
  },

  async updatePluginRelease(version, data) {
    const response = await api.patch(`/v1/plugin-releases/${version}`, data);
    return response.data;
  },

  async deletePluginRelease(version, { permanent = false } = {}) {
    const response = await api.delete(`/v1/plugin-releases/${version}${permanent ? '?permanent=true' : ''}`);
    return response.data;
  },

  async getPluginReleaseSettings() {
    const response = await api.get('/v1/plugin-releases/settings');
    return response.data;
  },

  async updatePluginReleaseSettings(data) {
    const response = await api.put('/v1/plugin-releases/settings', data);
    return response.data;
  },

  async getWeatherHistory(observatoryId, params = {}) {
    const response = await api.get(`/v1/weather-history/observatory/${observatoryId}`, { params });
    return response.data;
  },

  async getWeatherHistoryStats(observatoryId, params = {}) {
    const response = await api.get(`/v1/weather-history/observatory/${observatoryId}/stats`, { params });
    return response.data;
  },

  // External Storage (Issue #413)
  // Authoritative OAuth redirect URIs the server will use (honors
  // OAUTH_REDIRECT_BASE_URL), shown as the values to register in the provider.
  async getOAuthRedirectUris() {
    const response = await api.get('/v1/external-storage/oauth/redirect-uris');
    return response.data;
  },

  // Asterism's SSH public key — users add this to their remote server's
  // authorized_keys so the SFTP/SCP storage provider can connect.
  async getSshPublicKey() {
    const response = await api.get('/v1/external-storage/ssh-public-key');
    return response.data;
  },

  async getGdriveAuthUrl(clientId, clientSecret) {
    const response = await api.post('/v1/external-storage/gdrive/auth-url', {
      client_id: clientId,
      client_secret: clientSecret
    });
    return response.data;
  },

  async getGdriveTicketStatus(ticketId) {
    const response = await api.get(`/v1/external-storage/gdrive/ticket-status/${ticketId}`);
    return response.data;
  },

  async getDropboxAuthUrl(appKey, appSecret) {
    const response = await api.post('/v1/external-storage/dropbox/auth-url', {
      app_key: appKey,
      app_secret: appSecret
    });
    return response.data;
  },

  async getDropboxTicketStatus(ticketId) {
    const response = await api.get(`/v1/external-storage/dropbox/ticket-status/${ticketId}`);
    return response.data;
  },

  async getExternalStorageConfigs(params = {}) {
    const response = await api.get('/v1/external-storage/configs', { params });
    return response.data;
  },

  async getAvailableStorageConfigs() {
    const response = await api.get('/v1/external-storage/configs/available');
    return response.data;
  },

  // Organization Storage Provider (#1021): flatten the available-configs
  // organization map into a list a project can derive from.
  // Returns [{ _id, display_name, provider, destination_path, organization_id, organization_name }].
  async getOrgStorageProviders() {
    const res = await api.get('/v1/external-storage/configs/available');
    const orgMap = res.data?.data?.organization || {};
    const out = [];
    for (const [orgId, group] of Object.entries(orgMap)) {
      for (const cfg of (group.configs || [])) {
        out.push({
          _id: cfg._id,
          display_name: cfg.display_name,
          provider: cfg.provider,
          destination_path: cfg.destination_path,
          organization_id: orgId,
          organization_name: group.name,
        });
      }
    }
    return out;
  },

  async getOrganizationStorageConfigs(orgId) {
    const response = await api.get('/v1/external-storage/configs', {
      params: { scope: 'organization', owner_id: orgId }
    });
    return response.data;
  },

  async getProjectStorageConfigs(projectId) {
    const response = await api.get('/v1/external-storage/configs', {
      params: { scope: 'project', owner_id: projectId }
    });
    return response.data;
  },

  async createExternalStorageConfig(data) {
    const response = await api.post('/v1/external-storage/configs', data);
    return response.data;
  },

  async updateExternalStorageConfig(id, data) {
    const response = await api.put(`/v1/external-storage/configs/${id}`, data);
    return response.data;
  },

  async deleteExternalStorageConfig(id, { force = false } = {}) {
    // force=true acknowledges the org-config delete warning (#1021) and disables
    // any project configs that derive from it.
    const response = await api.delete(`/v1/external-storage/configs/${id}`, {
      params: force ? { force: 'true' } : undefined,
    });
    return response.data;
  },

  async testExternalStorageConfig(id) {
    const response = await api.post(`/v1/external-storage/configs/${id}/test`);
    return response.data;
  },

  async getExternalStorageTransfers(params = {}) {
    const response = await api.get('/v1/external-storage/transfers', { params });
    return response.data;
  },

  async retryExternalStorageTransfer(id) {
    const response = await api.post(`/v1/external-storage/transfers/${id}/retry`);
    return response.data;
  },

  // On-demand "Push Externally": queue transfers of an observation's or a
  // project's captured files to chosen destinations (need not be attached at
  // submission), optionally under a custom sub-path. Manual pushes never delete
  // the MinIO source. Provide exactly one of observation_id / project_id.
  async pushExternally({ config_ids, custom_path, content, observation_id, project_id, target, date, notify_summary, notify_on_complete }) {
    const response = await api.post('/v1/external-storage/push', {
      config_ids,
      custom_path,
      content,
      observation_id,
      project_id,
      target,
      date,
      notify_summary,
      notify_on_complete,
    }, {
      // The server returns 202 quickly now (#1231), but the synchronous half still authorizes the
      // scope and every destination config, which is several DB round trips. The global 10s default
      // is what surfaced as "timeout of 10000ms exceeded" on a 1,698-file push; this is headroom for
      // the validation phase, not for the scheduling, which no longer blocks the response.
      timeout: 60000,
    });
    return response.data;
  },

  // External-transfer insight + queue control (#1059)
  async getExternalTransferQueueStatus() {
    const response = await api.get('/v1/external-storage/insight/queue');
    return response.data;
  },

  async getActiveExternalTransfers(params = {}) {
    const response = await api.get('/v1/external-storage/insight/active', { params });
    return response.data;
  },

  async getExternalTransferStats(params = {}) {
    const response = await api.get('/v1/external-storage/insight/stats', { params });
    return response.data;
  },

  async getExternalTransferErrors(params = {}) {
    const response = await api.get('/v1/external-storage/insight/errors', { params });
    return response.data;
  },

  async pauseExternalTransfers() {
    const response = await api.post('/v1/external-storage/insight/queue/pause');
    return response.data;
  },

  async resumeExternalTransfers() {
    const response = await api.post('/v1/external-storage/insight/queue/resume');
    return response.data;
  },

  async reapStuckExternalTransfers(configId) {
    const response = await api.post('/v1/external-storage/insight/reap', configId ? { config_id: configId } : {});
    return response.data;
  },

  async deletePendingExternalTransfers(configId, reason) {
    const body = {};
    if (configId) body.config_id = configId;
    if (reason) body.reason = reason;
    const response = await api.post('/v1/external-storage/insight/delete-pending', body);
    return response.data;
  },

  // =========================================================================
  // Calibration
  // =========================================================================

  async getCalibrationLibrary(observatoryId, params = {}) {
    const response = await api.get(`/v1/calibration/library/${observatoryId}`, { params });
    return response.data;
  },

  async getCalibrationSummary(observatoryId) {
    const response = await api.get(`/v1/calibration/library/${observatoryId}/summary`);
    return response.data;
  },

  async getCalibrationFrame(frameId) {
    const response = await api.get(`/v1/calibration/frames/${frameId}`);
    return response.data;
  },

  async getCalibrationFrameComponents(frameId) {
    const response = await api.get(`/v1/calibration/frames/${frameId}/components`);
    return response.data;
  },

  async deleteCalibrationFrame(frameId, { soft = false } = {}) {
    const response = await api.delete(`/v1/calibration/frames/${frameId}${soft ? '?soft=true' : ''}`);
    return response.data;
  },

  async bulkDeleteCalibrationFrames(observatoryId, frameType) {
    const response = await api.delete(`/v1/calibration/frames/bulk?observatory_id=${observatoryId}&frame_type=${frameType}&confirm=true`);
    return response.data;
  },

  async batchDeleteCalibrationFrames(frameIds) {
    const response = await api.post('/v1/calibration/frames/batch-delete', { frame_ids: frameIds });
    return response.data;
  },

  async reactivateCalibrationFrame(frameId) {
    const response = await api.patch(`/v1/calibration/frames/${frameId}/reactivate`);
    return response.data;
  },

  async expireCalibrationFrame(frameId, expiredAt = null) {
    const body = expiredAt ? { expired_at: expiredAt } : {};
    const response = await api.patch(`/v1/calibration/frames/${frameId}/expire`, body);
    return response.data;
  },

  async uploadMasterFrame(formData, onUploadProgress) {
    const response = await api.post('/v1/calibration/masters/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
      onUploadProgress
    });
    return response.data;
  },

  async getCalibrationPolicy(observatoryId) {
    const response = await api.get(`/v1/calibration/policy/${observatoryId}`);
    return response.data;
  },

  async updateCalibrationPolicy(observatoryId, data) {
    const response = await api.put(`/v1/calibration/policy/${observatoryId}`, data);
    return response.data;
  },

  async expireDarks(observatoryId, { camera_name, readout_mode }) {
    const response = await api.post(`/v1/calibration/expire-darks/${observatoryId}`, { camera_name, readout_mode });
    return response.data;
  },

  async uploadIndividualFrame(formData, onUploadProgress) {
    const response = await api.post('/v1/calibration/frames', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
      onUploadProgress
    });
    return response.data;
  },

  async getCalibrationNeeds(observatoryId) {
    const response = await api.get(`/v1/calibration/needs/${observatoryId}`);
    return response.data;
  },

  async getCalibrationReadiness(observatoryId, params = {}) {
    const response = await api.get(`/v1/calibration/readiness/${observatoryId}`, { params });
    return response.data;
  },

  async getCalibrationCameras(observatoryId) {
    const response = await api.get(`/v1/calibration/cameras/${observatoryId}`);
    return response.data;
  },

  async createMasterFrame(params) {
    const response = await api.post('/v1/calibration/masters/create', params);
    return response.data;
  },

  async releaseConsumedFrames(observatoryId) {
    const response = await api.post(`/v1/calibration/release-consumed/${observatoryId}`);
    return response.data;
  },

  // Calibration Maintenance
  async getCalibrationHealth(params = {}) {
    const response = await api.get('/v1/calibration/health', { params });
    return response.data;
  },
  async triggerExpiryCheck() {
    const response = await api.post('/v1/calibration/maintenance/expire');
    return response.data;
  },
  async triggerRetentionCleanup() {
    const response = await api.post('/v1/calibration/maintenance/cleanup');
    return response.data;
  },
  async clearCalibrationCache() {
    const response = await api.delete('/v1/calibration/cache');
    return response.data;
  },
  async getCalibrationCacheStats() {
    const response = await api.get('/v1/calibration/cache/stats');
    return response.data;
  },
  async supersedeNullReadoutMasters(observatoryId) {
    const params = observatoryId ? { observatory_id: observatoryId } : {};
    const response = await api.post('/v1/calibration/maintenance/supersede-null-readout', {}, { params });
    return response.data;
  },
  async purgeNullReadoutFrames(observatoryId) {
    const params = observatoryId ? { observatory_id: observatoryId } : {};
    const response = await api.post('/v1/calibration/maintenance/purge-null-readout-frames', {}, { params });
    return response.data;
  },
  async resetCalibration(observatoryId, { deleteFiles = false, deleteObservations = false } = {}) {
    const params = { confirm: 'true' };
    if (deleteFiles) params.delete_files = 'true';
    if (deleteObservations) params.delete_observations = 'true';
    const response = await api.delete(`/v1/calibration/reset/${observatoryId}`, { params });
    return response.data;
  },

  // Delete all master frames for an observatory while preserving individuals.
  // The 5-minute master sweep will restack new masters using the current
  // filename scheme. See issue #600.
  async rebuildMasters(observatoryId) {
    // Pass an empty object (not null) so axios doesn't send a bare `null`
    // body — the API's strict JSON body parser rejects that.
    const response = await api.post(`/v1/calibration/rebuild-masters/${observatoryId}`, {}, {
      params: { confirm: 'true' },
    });
    return response.data;
  },

  // Force-rebuild previews for an observation. Clears preview_url,
  // thumbnail_url, preview_calibrated on each matched UserFile and submits
  // pipeline jobs to regenerate. Use calibratedOnly=true to scope to files
  // that were previously calibrated — the typical case after a master-level
  // fix. See issue #598.
  //
  // Timeout is raised to 5 minutes because a large observation (hundreds of
  // files) can take the server a while to enqueue all the pipeline jobs
  // even with concurrent submission. The default axios timeout (10s) is
  // too tight for that batch.
  async rebuildObservationPreviews(observationId, { calibratedOnly = false } = {}) {
    const params = {};
    if (calibratedOnly) params.calibrated_only = 'true';
    const response = await api.post(
      `/v1/processing/rebuild-previews/${observationId}`,
      {},
      { params, timeout: 5 * 60 * 1000 }
    );
    return response.data;
  },
};

export default apiService;